// IMPORT REACT
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../theme/style.scss";
import "react-quill/dist/quill.snow.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReactQuill from "react-quill";

// IMPORT SERVICE
import { AlertType } from "../../services/models/AlertType";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import {
  GetInstructionDetail,
  CreateInstruction,
  UpdateInstruction,
} from "../../services/InstructionService";
import { PreferencesContext } from "../../PreferenceContext";
import { CustomSelect } from "../../components/CustomFields/CustomSelect";
import { GetSpecialityList } from "../../services/SpecialityService";
import { GetDepartmentList } from "../../services/DepartmentService";
import { PAGE_SIZE } from "../../services/common/const";
import { optionInstructionType } from "../../shared/Data/OPD/InstructionTemplateType/InstructionTemplateType";
import { SpeechMic } from "../../components/SpeechToText";

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

// SCHEMA
interface InstructionDetail {
  instructionDetailId: number;
  instructionId: number;
  instructionLanguageCode: string;
  instructionLanguage: string;
  instructionData: string;
  isDeleted: boolean;
  mode: string;
}

// SCHEMA FOR FORM
interface formInitialValueTypes {
  instructionId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  departmentId: selectOption | null;
  specialityId: selectOption | null;
  instructionTypeCode: String;
  instructionDetail: Array<InstructionDetail> | null;
  isDeleted: boolean;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  specialityId: string;
  departmentId: string;
  branchId: string;
  orgId: string;
  consumerId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  specialityId: "",
  departmentId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  orgId: "",
  consumerId: "",
};

export default function InstructionSave(Props: any) {
  // PROPS
  const {
    isForPopUp = false,
    handleClosePopUp,
    name,
    instructionType,
    departmentObject,
    specilattyObject,
  } = Props;

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const quillRefs = useRef<{ [key: number]: ReactQuill | null }>({});

  // DEFAULT SCHEMA VALUE FOR FORM
  const initInstructionData: formInitialValueTypes = {
    instructionId: 0,
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    departmentId:
      userContext?.departmentId > 0
        ? {
          label: userContext?.departmentName || "",
          value: userContext?.departmentId?.toString(),
        }
        : null,
    specialityId:
      userContext?.specialityId > 0
        ? {
          label: userContext?.specialityName || "",
          value: userContext?.specialityId?.toString(),
        }
        : null,
    instructionTypeCode: isForPopUp ? name : "",
    instructionDetail: null,
    isDeleted: false,
  };

  const [param, setParam] = useState(parameters);
  const [departmentData, setdepartmentData] = useState<any[]>([]);
  const [specialityData, setspecialityData] = useState<any[]>([]);

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawInstructionId = urlParams.get("q");
  let InstructionType: any = urlParams.get("type");

  let curInstructionId = 0;
  if (
    ((curRawInstructionId !== null && curRawInstructionId !== undefined) ||
      (InstructionType !== null && InstructionType !== undefined)) &&
    isForPopUp === false
  ) {
    let decId = edService.decrypt(curRawInstructionId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      curInstructionId = +decId;
    }
  } else {
    if (urlParams.size > 0 && isForPopUp === false) {
      // navigate("/not-found");
    }
  }

  if (isForPopUp && instructionType) {
    InstructionType = instructionType;
  }

  // VARIABLES -- STATIC DATA
  const instructionTypeCode = optionInstructionType.find(
    (x) => x.type === InstructionType
  );

  // IF NOT FIND THEN NAVIGATE TO NOT FOUND PAGE  --- PENDING -- ASK TO SIR ABOUT THIS

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    instructionId: Yup.number().nullable(),
    orgId: Yup.number().required(),
    consumerId: Yup.number().required(),
    branchId: Yup.number().required(),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Department is required"),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Speciality is required"),
    instructionTypeCode: Yup.string(),
    instructionDetail: Yup.array()
      .of(
        Yup.object().shape({
          instructionLanguage: Yup.string().required(
            "Instruction Language is required."
          ),
          instructionLanguageCode: Yup.string().required(
            "Instruction Language Code is required."
          ),
          instructionData: Yup.string()
            .transform((value) => {
              // Convert empty ReactQuill content to null
              if (!value) return null;
              // Remove all empty paragraphs and check if anything remains
              const cleanValue = value.replace(/<p><br><\/p>/g, "").trim();
              return cleanValue === "" ? null : value;
            })
            .when("instructionLanguageCode", {
              is: (val: string) => val === "ENGLISH-IN",
              then: (schema) => schema.required("Instruction is required."),
              otherwise: (schema) => schema.nullable(),
            }),
        })
      )
      .required("Required"),
    isLocked: Yup.boolean(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initInstructionData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // GET DEPARTMENT
  async function fetchDepartment() {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const departmentData: any = await GetDepartmentList(params);
      if (departmentData?.data?.data?.departmentColl) {
        let departmentCollection = departmentData.data.data.departmentColl;
        setdepartmentData(departmentCollection);
      }
      // setIsLoading(false);
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
      // setIsLoading(false);
    }
  }

  // GET SPECIALITY
  async function fetchSpeciality(departmentIds: string) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const specialityData: any = await GetSpecialityList(params);
      if (specialityData?.data?.data?.specialityColl) {
        let specialityCollection = specialityData.data.data.specialityColl;
        setspecialityData(specialityCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET Medicine Diluent Name DETAIL
  const getInstructionDetailById = async () => {
    let Instruction: any;
    let params = new URLSearchParams();
    params.append("instructionId", curInstructionId?.toString());
    params.append("ConsumerId", userContext?.consumerId?.toString());
    params.append("OrgId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    try {
      const res: any = await GetInstructionDetail(params);
      Instruction = res.data.data;
      if (curInstructionId > 0) {
        methods.reset({
          ...initInstructionData,
          instructionId: Instruction?.instructionId || 0,
          instructionTypeCode: Instruction?.instructionTypeCode || "",
          instructionDetail: Instruction?.instructionDetailCollection,
          departmentId: Instruction?.departmentId
            ? {
              label: Instruction?.departmentName,
              value: Instruction?.departmentId,
            }
            : null,
          specialityId: Instruction?.specialityId
            ? {
              label: Instruction?.specialityName,
              value: Instruction?.specialityId,
            }
            : null,
        });
      } else {
        const instructionDetailWithName = Instruction?.instructionDetailCollection?.map(
          (item: any) => {
            if (item?.instructionLanguageCode === "ENGLISH-IN" && isForPopUp && name) {
              return { ...item, instructionData: name };
            }
            return item;
          }
        );

        methods.setValue("instructionDetail", instructionDetailWithName, {
          shouldValidate: true,
        });
      }

      return Instruction;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE INSTRUCTION
  const saveInstruction = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let tempInstructionDetailData: any[] = [];
      value.instructionDetail?.forEach((x: any) => {
        tempInstructionDetailData.push({
          instructionDetailId: x.instructionDetailId,
          instructionId: x.instructionId,
          instructionLanguageCode: x.instructionLanguageCode,
          instructionData: x.instructionData,
          isDeleted: false,
          mode: x.instructionDetailId > 0 ? "U" : "I",
        });
      });

      let formData = {
        instructionId: value.instructionId,
        specialityId: value.specialityId?.value || 0,
        departmentId: value.departmentId?.value || 0,
        branchId: userContext.branchId.toString(),
        orgId: userContext.organizationId.toString(),
        consumerId: userContext.consumerId.toString(),
        instructionTypeCode: instructionTypeCode?.value?.toString(),
        DataManagedBy: userContext.loginDetail.userId,
        insturctionDetailData: JSON.stringify(tempInstructionDetailData),
      };
      if (curInstructionId > 0) {
        result = await UpdateInstruction(formData);
      } else {
        result = await CreateInstruction(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          const data = {
            instructionId: result?.data?.instructionId,
            instructionData: name,
            instructionDetailData: tempInstructionDetailData,
          }
          handleClosePopUp("INSTUCTION_CREATE", data);
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveInstruction(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (curInstructionId <= 0) {
        await fetchDepartment();
      }
      if (curInstructionId > 0) {
        const Instruction = await getInstructionDetailById();
        // await fetchSpeciality(Instruction?.departmentId);
      } else if (userContext?.departmentId > 0) {
        await fetchSpeciality(userContext?.departmentId?.toString());
        if (userContext?.specialityId > 0) {
          await getInstructionDetailById();
        }
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curInstructionId]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <>
            <Grid container className="page-header">
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  <CustomBreadcrumb />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  {/* <Grid item xs={12}>
                    <Typography variant="h6">Basic Information</Typography>
                  </Grid> */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.departmentId
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Department *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="departmentId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"departmentId"}
                              FieldName={"departmentId"}
                              options={departmentData}
                              isDisabled={curInstructionId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.departmentId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "departmentId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue("specialityId", null, {
                                    shouldValidate: true,
                                  });
                                  methods.setValue("instructionDetail", null, {
                                    shouldValidate: true,
                                  });
                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchSpeciality(e.value);
                                    setIsLoading(false);
                                  } else {
                                    setspecialityData([]);
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.departmentId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.specialityId
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Speciality *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="specialityId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"specialityId"}
                              FieldName={"specialityId"}
                              options={specialityData}
                              isDisabled={curInstructionId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.specialityId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "specialityId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  if (e?.value) {
                                    setIsLoading(true);
                                    await getInstructionDetailById();
                                    setIsLoading(false);
                                  } else {
                                    methods.setValue(
                                      "instructionDetail",
                                      null,
                                      {
                                        shouldValidate: true,
                                      }
                                    );
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.specialityId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    md={3}
                    display={{
                      xs: "none",
                      md: "grid",
                    }}
                  ></Grid>
                  {methods
                    .getValues()
                    ?.instructionDetail?.map(
                      (item: InstructionDetail, index: number) => (
                        <Grid item xs={12} sm={12} md={6} key={index}>
                          <FormControl fullWidth className="from-controller">
                            <FormLabel className="fileds-label-text">
                              {instructionTypeCode?.label?.toString()} -{" "}
                              {item?.instructionLanguage}
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-box"
                            >
                              <Box sx={{ position: "relative" }}>
                                <Controller
                                  control={methods.control}
                                  name={`instructionDetail.${index}.instructionData`}
                                  render={({ field }) => (
                                    <ReactQuill
                                      {...field}
                                      ref={(el) => (quillRefs.current[index] = el)}
                                      theme="snow"
                                      value={item.instructionData}
                                      // value={
                                      //   isForPopUp && index == 0
                                      //     ? name
                                      //     : item.instructionData
                                      // }
                                      className={`react-quill-alignment`}
                                    />
                                  )}
                                />
                                <Box
                                  sx={{
                                    position: "absolute",
                                    bottom: "8px",
                                    right: "8px",
                                    zIndex: 10,
                                  }}
                                >
                                  <SpeechMic
                                    onTranscript={(text) => {
                                      const quill = quillRefs.current[index]?.getEditor();
                                      if (quill) {
                                        const range = quill.getSelection();
                                        const position = range ? range.index : quill.getLength();
                                        quill.insertText(position, text + " ");
                                        quill.setSelection({ index: position + text.length + 1, length: 0 });
                                      }
                                    }}
                                    size="small"
                                    disabled={isLoading}
                                  />
                                </Box>
                              </Box>
                              <Typography className="validation-message">
                                {methods.formState.errors.instructionDetail &&
                                  methods.formState.errors.instructionDetail[
                                    index
                                  ]?.instructionData?.message}
                              </Typography>
                            </SkeletonWrapper>
                          </FormControl>
                        </Grid>
                      )
                    )}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={isLoading || methods.formState.isSubmitting}
                  >
                    {curInstructionId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                    disabled={isLoading || methods.formState.isSubmitting}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
